/**
 * Helligkeit aus dem Luftbild abtasten (PV-D19, PV-D23).
 *
 * Die Erkennung laeuft auf dem Geraet, nicht auf dem Server — sonst
 * funktioniert sie offline nicht, und genau dort, beim Kunden, wird sie
 * gebraucht. Die Kacheln liegen ohnehin schon lokal, weil sie beim Vorbereiten
 * des Projekts geladen wurden.
 *
 * Abgetastet wird nur der Ausschnitt um das Gebaeude, nicht der ganze Umkreis.
 * Ein 400-m-Ausschnitt in hoechster Zoomstufe waere ein Bild von 4000 mal 4000
 * Punkten — 64 MB Bilddaten fuer eine Frage, die ein Dach beantwortet.
 */

import {
  nachGeo,
  type Farbprobe,
  type Geo,
  type Helligkeitsprobe,
  type Punkt,
} from '@pv/geometrie';

import { kachelUrl } from './kacheln.ts';

/** Groesster Ausschnitt, den wir uns leisten. Darueber wird die Zoomstufe gesenkt. */
const MAX_KANTE_PX = 1600;
const KACHELGROESSE = 256;

interface Weltpunkt {
  x: number;
  y: number;
}

function weltpixel(p: Geo, zoom: number): Weltpunkt {
  const n = KACHELGROESSE * 2 ** zoom;
  const sin = Math.sin((p.lat * Math.PI) / 180);
  return {
    x: ((p.lng + 180) / 360) * n,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * n,
  };
}

export interface Bildausschnitt {
  probe: Helligkeitsprobe;
  /** Dieselbe Stelle als Farbe — die Dacherkennung braucht mehr als hell/dunkel. */
  farbe: Farbprobe;
  zoom: number;
  /** Wie viele der noetigen Kacheln tatsaechlich da waren. */
  kachelnGeladen: number;
  kachelnGesamt: number;
}

/**
 * Bildausschnitt um einen Umriss aufbauen und eine Probefunktion liefern.
 *
 * Die Probefunktion nimmt Punkte im Grundriss-Koordinatensystem des uebergebenen
 * Bezugspunkts entgegen — also genau das, womit der Geometriekern rechnet.
 */
export async function bildausschnitt(
  umriss: Geo[],
  bezug: Geo,
  vorlage: string,
  maxZoom: number,
  randM = 5,
): Promise<Bildausschnitt | undefined> {
  if (umriss.length < 3) return undefined;

  const gradBreite = randM / 111_320;
  const gradLaenge = randM / (111_320 * Math.cos((bezug.lat * Math.PI) / 180));
  const nord = Math.max(...umriss.map((p) => p.lat)) + gradBreite;
  const sued = Math.min(...umriss.map((p) => p.lat)) - gradBreite;
  const west = Math.min(...umriss.map((p) => p.lng)) - gradLaenge;
  const ost = Math.max(...umriss.map((p) => p.lng)) + gradLaenge;

  // Zoomstufe so hoch wie moeglich, aber so, dass das Bild handhabbar bleibt.
  let zoom = maxZoom;
  while (zoom > 14) {
    const a = weltpixel({ lat: nord, lng: west }, zoom);
    const b = weltpixel({ lat: sued, lng: ost }, zoom);
    if (Math.max(b.x - a.x, b.y - a.y) <= MAX_KANTE_PX) break;
    zoom--;
  }

  const linksOben = weltpixel({ lat: nord, lng: west }, zoom);
  const rechtsUnten = weltpixel({ lat: sued, lng: ost }, zoom);
  const vonX = Math.floor(linksOben.x / KACHELGROESSE);
  const bisX = Math.floor(rechtsUnten.x / KACHELGROESSE);
  const vonY = Math.floor(linksOben.y / KACHELGROESSE);
  const bisY = Math.floor(rechtsUnten.y / KACHELGROESSE);

  const breite = (bisX - vonX + 1) * KACHELGROESSE;
  const hoehe = (bisY - vonY + 1) * KACHELGROESSE;
  const leinwand = document.createElement('canvas');
  leinwand.width = breite;
  leinwand.height = hoehe;
  const stift = leinwand.getContext('2d', { willReadFrequently: true });
  if (!stift) return undefined;

  let geladen = 0;
  let gesamt = 0;
  for (let x = vonX; x <= bisX; x++) {
    for (let y = vonY; y <= bisY; y++) {
      gesamt++;
      try {
        const antwort = await fetch(kachelUrl(vorlage, { z: zoom, x, y }), { mode: 'cors' });
        if (!antwort.ok) continue;
        const bild = await createImageBitmap(await antwort.blob());
        stift.drawImage(bild, (x - vonX) * KACHELGROESSE, (y - vonY) * KACHELGROESSE);
        bild.close();
        geladen++;
      } catch {
        // Fehlende Kachel heisst: an dieser Stelle keine Probe. Die Rechnung
        // kommt damit zurecht, sie zaehlt nur die Punkte, die sie bekommen hat.
      }
    }
  }
  if (geladen === 0) return undefined;

  const bilddaten = stift.getImageData(0, 0, breite, hoehe).data;
  const ursprungX = vonX * KACHELGROESSE;
  const ursprungY = vonY * KACHELGROESSE;

  const probe: Helligkeitsprobe = (p: Punkt) => {
    const welt = weltpixel(nachGeo(p, bezug), zoom);
    const px = Math.round(welt.x - ursprungX);
    const py = Math.round(welt.y - ursprungY);
    if (px < 0 || py < 0 || px >= breite || py >= hoehe) return undefined;
    const stelle = (py * breite + px) * 4;
    const alpha = bilddaten[stelle + 3] ?? 0;
    if (alpha === 0) return undefined;
    const r = bilddaten[stelle] ?? 0;
    const g = bilddaten[stelle + 1] ?? 0;
    const b = bilddaten[stelle + 2] ?? 0;
    return 0.299 * r + 0.587 * g + 0.114 * b;
  };

  // Dieselbe Rechnung, nur ohne das Zusammenziehen auf einen Grauwert. Ein
  // rotes Ziegeldach und eine Wiese sind aehnlich hell und voellig verschieden
  // gefaerbt — fuer die Dacherkennung ist das der Unterschied.
  const farbe: Farbprobe = (p: Punkt) => {
    const welt = weltpixel(nachGeo(p, bezug), zoom);
    const px = Math.round(welt.x - ursprungX);
    const py = Math.round(welt.y - ursprungY);
    if (px < 0 || py < 0 || px >= breite || py >= hoehe) return undefined;
    const stelle = (py * breite + px) * 4;
    if ((bilddaten[stelle + 3] ?? 0) === 0) return undefined;
    return [bilddaten[stelle] ?? 0, bilddaten[stelle + 1] ?? 0, bilddaten[stelle + 2] ?? 0];
  };

  return { probe, farbe, zoom, kachelnGeladen: geladen, kachelnGesamt: gesamt };
}
